import React, { useState } from 'react';
import { AlertTriangle, ShieldCheck, Scale, BookOpen } from 'lucide-react';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { antisemitismLabels, contentRoleLabels, jdaClassificationLabels } from '../lib/data';
import type { AntisemitismAnalysis, ContentRole, FrameworkLevel, JdaClassification } from '../lib/data';
const ConfidenceDot = ({
  level
}: {
  level: 'high' | 'medium' | 'low';
}) => {
  const colors = {
    high: 'bg-sentiment-positive',
    medium: 'bg-yellow-500',
    low: 'bg-sentiment-negative'
  };
  const labels = {
    high: 'alta',
    medium: 'media',
    low: 'bassa'
  };
  return <span className={`w-2 h-2 rounded-full ${colors[level]} flex-shrink-0`} title={`Confidenza: ${labels[level]}`} data-id="element-108" />;
};
function levelChipClasses(level: FrameworkLevel) {
  switch (level) {
    case 'high':
      return 'bg-red-50 text-red-700';
    case 'medium':
      return 'bg-orange-50 text-orange-700';
    case 'low':
      return 'bg-amber-50 text-amber-700';
    default:
      return 'bg-emerald-50 text-emerald-700';
  }
}
function levelDotClasses(level: FrameworkLevel) {
  switch (level) {
    case 'high':
      return 'bg-red-500';
    case 'medium':
      return 'bg-orange-500';
    case 'low':
      return 'bg-amber-500';
    default:
      return 'bg-emerald-500';
  }
}
function LevelBadge({
  level,
  score
}: {
  level: FrameworkLevel;
  score: number;
}) {
  return <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${levelChipClasses(level)}`} data-id="element-109">
      <span className={`w-1.5 h-1.5 rounded-full ${levelDotClasses(level)}`} data-id="element-110" />
      {antisemitismLabels[level]}
      {level !== 'none' && <span style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-111">
          · {score.toFixed(1)}
        </span>}
    </span>;
}
interface FrameworkOverride {
  level: FrameworkLevel;
  note: string;
}
const LEVEL_OPTIONS: FrameworkLevel[] = ['none', 'low', 'medium', 'high'];
function FrameworkCard({
  icon: Icon,
  title,
  level,
  score,
  confidence,
  matched,
  rationale,
  badge,
  override,
  onOverrideChange
}: {
  icon: React.ElementType;
  title: string;
  level: FrameworkLevel;
  score: number;
  confidence: 'high' | 'medium' | 'low';
  matched: string[];
  rationale: string;
  badge?: React.ReactNode;
  override: FrameworkOverride;
  onOverrideChange: (o: FrameworkOverride) => void;
}) {
  return <div className="border border-border rounded-lg bg-card p-4 flex flex-col gap-3" data-id="element-112">
      <div className="flex items-start justify-between gap-2" data-id="element-113">
        <div className="flex items-center gap-2 min-w-0" data-id="element-114">
          <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" data-id="element-115" />
          <span className="text-sm font-medium truncate" data-id="element-116">{title}</span>
        </div>
        <ConfidenceDot level={confidence} data-id="element-117" />
      </div>

      <div className="flex flex-wrap items-center gap-2" data-id="element-118">
        <LevelBadge level={level} score={score} data-id="element-119" />
        {badge}
      </div>

      {matched.length > 0 && <div className="flex flex-wrap gap-1.5" data-id="element-120">
          {matched.map(m => <Badge key={m} variant="outline" className="text-[10px] font-normal px-1.5 py-0" data-id="element-121">
              {m}
            </Badge>)}
        </div>}

      <p className="text-xs text-muted-foreground leading-relaxed" data-id="element-122">
        {rationale}
      </p>

      <Separator data-id="element-123" />

      {/* Analyst override */}
      <div className="space-y-2" data-id="element-124">
        <div className="grid grid-cols-[auto_1fr] gap-2 items-center" data-id="element-125">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-heading" data-id="element-126">
            Correggi livello
          </span>
          <Select value={override.level} onValueChange={v => onOverrideChange({
          ...override,
          level: v as FrameworkLevel
        })} data-id="element-127">
            <SelectTrigger className="h-8 text-xs" data-id="element-128">
              <SelectValue data-id="element-129" />
            </SelectTrigger>
            <SelectContent data-id="element-130">
              {LEVEL_OPTIONS.map(l => <SelectItem key={l} value={l} className="text-xs" data-id="element-131">
                  {antisemitismLabels[l]}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <input type="text" value={override.note} onChange={e => onOverrideChange({
        ...override,
        note: e.target.value
      })} placeholder="Nota dell'analista (opzionale)…" className="w-full h-8 rounded-md border border-input bg-background px-2.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-id="element-132" />
      </div>
    </div>;
}
export function AntisemitismPanel({
  analysis
}: {
  analysis: AntisemitismAnalysis;
}) {
  const [contentRole, setContentRole] = useState<ContentRole>(analysis.aggregate.contentRole);
  const [ihraOv, setIhraOv] = useState<FrameworkOverride>({
    level: analysis.ihra.level,
    note: ''
  });
  const [jdaOv, setJdaOv] = useState<FrameworkOverride>({
    level: analysis.jda.level,
    note: ''
  });
  const [deonOv, setDeonOv] = useState<FrameworkOverride>({
    level: analysis.deontologico.level,
    note: ''
  });
  const jdaClass: JdaClassification = analysis.jda.classification;
  const isProtected = jdaClass === 'protected_criticism';
  return <div className="space-y-4" data-id="element-133">
      {/* Header: aggregate + content role */}
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-4" data-id="element-134">
        <div className="flex items-center justify-between gap-3" data-id="element-135">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider font-heading" data-id="element-136">
            Analisi Antisemitismo
          </span>
          <LevelBadge level={analysis.aggregate.level} score={analysis.aggregate.score} data-id="element-137" />
        </div>
        <div className="grid grid-cols-[auto_1fr] items-center gap-2" data-id="element-138">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-heading" data-id="element-139">
            Ruolo del contenuto
          </span>
          <Select value={contentRole} onValueChange={v => setContentRole(v as ContentRole)} data-id="element-140">
            <SelectTrigger className="h-8 text-xs" data-id="element-141">
              <SelectValue data-id="element-142" />
            </SelectTrigger>
            <SelectContent data-id="element-143">
              {(['reported', 'endorsed', 'ambiguous'] as ContentRole[]).map(r => <SelectItem key={r} value={r} className="text-xs" data-id="element-144">
                    {contentRoleLabels[r]}
                  </SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {contentRole === 'reported' && <p className="text-[11px] text-muted-foreground leading-relaxed" data-id="element-145">
            Contenuto d'odio riportato a fini di cronaca: non costituisce
            condotta problematica della testata.
          </p>}
      </div>

      {/* Disagreement banner */}
      {analysis.aggregate.hasDisagreement && <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5" data-id="element-146">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-147" />
          <p className="text-xs text-amber-800 leading-relaxed" data-id="element-148">
            <span className="font-medium" data-id="element-149">Discordanza tra definizioni</span> — i
            quadri di riferimento divergono. Verifica manuale consigliata.
          </p>
        </div>}

      {/* Protected criticism notice */}
      {isProtected && <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-id="element-150">
          <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" data-id="element-151" />
          <p className="text-xs text-emerald-800 leading-relaxed" data-id="element-152">
            <span className="font-medium" data-id="element-153">Critica legittima</span> — secondo la
            JDA (linee guida 11–15) non costituisce antisemitismo e non va
            segnalata come violazione.
          </p>
        </div>}

      {/* Three framework cards */}
      <div className="grid grid-cols-1 gap-3" data-id="element-154">
        <FrameworkCard icon={BookOpen} title="IHRA" level={analysis.ihra.level} score={analysis.ihra.score} confidence={analysis.ihra.confidence} matched={analysis.ihra.matched} rationale={analysis.ihra.rationale} override={ihraOv} onOverrideChange={setIhraOv} data-id="element-155" />
        <FrameworkCard icon={Scale} title="JDA — Jerusalem Declaration" level={analysis.jda.level} score={analysis.jda.score} confidence={analysis.jda.confidence} matched={analysis.jda.matched} rationale={analysis.jda.rationale} badge={<span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full ${isProtected ? 'bg-emerald-50 text-emerald-700' : jdaClass === 'antisemitic' ? 'bg-red-50 text-red-700' : 'bg-muted text-muted-foreground'}`} data-id="element-157">
              {jdaClassificationLabels[jdaClass]}
            </span>} override={jdaOv} onOverrideChange={setJdaOv} data-id="element-156" />
        <FrameworkCard icon={Scale} title="Codice deontologico" level={analysis.deontologico.level} score={analysis.deontologico.score} confidence={analysis.deontologico.confidence} matched={analysis.deontologico.matched} rationale={analysis.deontologico.rationale} badge={analysis.deontologico.isJournalistConduct ? <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-700" data-id="element-159">
                Condotta giornalistica
              </span> : undefined} override={deonOv} onOverrideChange={setDeonOv} data-id="element-158" />
      </div>
    </div>;
}